/**
 * Confirm Modal Component
 * Standardized confirmation dialogs for destructive actions
 */

import React from 'react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { colors } from '../../theme/designTokens';

/**
 * ConfirmModal Component
 *
 * @param {boolean} visible - Modal visibility (from useModal)
 * @param {function} onConfirm - Confirm handler
 * @param {function} onCancel - Cancel handler
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Confirm button text (default: 'Confirm')
 * @param {string} cancelText - Cancel button text (default: 'Cancel')
 * @param {boolean} danger - Show danger styling (default: true)
 * @param {boolean} loading - Show loading state on confirm
 * @param {ReactNode} children - Additional content
 *
 * @example
 * const modal = useModal();
 * <ConfirmModal
 *   visible={modal.visible}
 *   onCancel={modal.close}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 * />
 */
function ConfirmModal({
  visible,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = true,
  loading = false,
  children,
  ...restProps
}) {
  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <span>
          <ExclamationCircleOutlined
            style={{
              color: danger ? colors.status.fail : colors.status.warning,
              marginRight: 8,
            }}
          />
          {title}
        </span>
      }
      onOk={handleConfirm}
      onCancel={onCancel}
      okText={confirmText}
      cancelText={cancelText}
      okButtonProps={{
        danger,
        loading,
      }}
      centered
      {...restProps}
    >
      {message && <p>{message}</p>}
      {children}
    </Modal>
  );
}

/**
 * Functional API for quick confirmations
 * Alternative to component usage
 */
export const confirmDialog = ({
  title = 'Confirm Action',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = true,
}) => {
  Modal.confirm({
    title,
    content: message,
    okText: confirmText,
    cancelText,
    okButtonProps: { danger },
    onOk: onConfirm,
    onCancel,
    centered: true,
    icon: (
      <ExclamationCircleOutlined
        style={{
          color: danger ? colors.status.fail : colors.status.warning,
        }}
      />
    ),
  });
};

export default ConfirmModal;
